<template>
  <div class="claim-media">
    <div v-for="(m, index) of media" :key="m.id">
      <div
        v-if="displayOverlay(index)"
        class="overlay"
        @click="expandMedia"
        :class="{ private: anyPrivate }"
      >
        +{{ expandIndicator }}
      </div>
      <img
        :src="m.thumbnail"
        :alt="m.id"
        :class="{ hide: pivotPoint < index, private: m.isPrivate }"
        @click="selectMedia(m)"
        :title="m.isPrivate ? 'Hidden from vendor' : 'Shared with vendor'"
      />
    </div>
  </div>
  <modal class="modal-claim-media" v-if="isModalVisible" @close="onCloseModal">
    <claim-media-gallery-viewer
      :media="media"
      :is-sharable="isSharable"
      :selected-media="currentMedia"
      @toggle-share="toggleShare"
    />
  </modal>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue';
import Modal from '@/components/Modal.vue';
import ClaimMediaGalleryViewer from '@/components/ClaimMediaGalleryViewer.vue';
import { IClaimMedia } from '@/types';

export default defineComponent({
  name: 'ClaimMediaGallery',
  components: {
    ClaimMediaGalleryViewer,
    Modal,
  },
  props: {
    media: Array,
    isSharable: {
      // flag to display the share with vendor checkbox
      type: Boolean,
      default: true,
    },
  },
  emits: ['toggleShare'],
  setup(props, { emit }) {
    const pivotPoint = ref<number>(4);
    const expandIndicator = computed<number>(() =>
      props.media ? props.media.length - 1 - pivotPoint.value : 0
    );
    const anyPrivate = computed<boolean>(() => {
      const mediaArr =
        props.media && props.media.length > pivotPoint.value
          ? props.media.slice(pivotPoint.value)
          : [];
      return mediaArr.some((media: any) => media.isPrivate === true);
    });

    const isModalVisible = ref<boolean>(false);

    const currentMedia = ref<IClaimMedia | null>(null);

    const displayOverlay = (index: number): boolean =>
      expandIndicator.value > 0 && index === pivotPoint.value;

    const expandMedia = () => {
      pivotPoint.value = props.media ? props.media?.length + 1 : 4;
    };

    const onCloseModal = () => {
      isModalVisible.value = false;
    };

    const selectMedia = (media: IClaimMedia) => {
      currentMedia.value = media;
      isModalVisible.value = true;
    };

    const toggleShare = (media: IClaimMedia) => {
      // have to define by using camelCase, due to the browser warning
      // [Vue warn]: Extraneous non-emits event listeners (toggleShare) were passed to component but could not be automatically inherited because component renders fragment or text root nodes. If the listener is intended to be a component custom event listener only, declare it using the "emits" option.
      // eslint-disable-next-line vue/custom-event-name-casing
      emit('toggleShare', media);
    };

    return {
      pivotPoint,
      displayOverlay,
      expandIndicator,
      expandMedia,
      currentMedia,
      selectMedia,
      isModalVisible,
      onCloseModal,
      toggleShare,
      anyPrivate,
    };
  },
});
</script>

<style lang="scss">
.claim-media {
  display: flex;
  flex-wrap: wrap;
  position: relative;

  .overlay {
    position: absolute;
    z-index: 9;
    width: 5rem;
    height: 5rem;
    background-color: rgba(#000, 0.5);
    color: var(--white);
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: var(--font-size-24);
    cursor: pointer;

    &.private {
      box-sizing: border-box;
      border-bottom: 5px solid var(--coral);
    }
  }

  img {
    width: 5rem;
    height: 5rem;
    margin-right: var(--space-1);
    margin-bottom: var(--space-1);
    cursor: pointer;

    &.hide {
      display: none;
    }

    &.private {
      box-sizing: border-box;
      border-bottom: 5px solid var(--coral);
    }
  }
}

.modal.modal-claim-media {
  .body {
    height: 95vh;
    max-width: 95%;
    width: 95%;
  }
}
</style>
